import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Clock, 
  Phone, 
  Star, 
  ArrowLeft, 
  Navigation,
  Search,
  Filter
} from 'lucide-react';
import { publicAPI } from '../api';
import { NearbyShop } from '../types';

const FindShops: React.FC = () => {
  const navigate = useNavigate();
  const [shops, setShops] = useState<NearbyShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    getCurrentLocationAndFindShops();
  }, []);

  const getCurrentLocationAndFindShops = async () => {
    try {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ lat: latitude, lng: longitude });
            await findNearbyShops(latitude, longitude);
          },
          (error) => {
            console.error('Geolocation error:', error);
            setError('Location access denied. Showing all shops.');
            findNearbyShops();
          }
        );
      } else {
        findNearbyShops();
      }
    } catch (err) {
      setError('Failed to find nearby shops');
      setLoading(false);
    }
  };

  const findNearbyShops = async (lat?: number, lng?: number) => {
    try {
      const params: any = {};
      if (lat && lng) {
        params.lat = lat;
        params.lng = lng;
        params.radius = 20; // 20km radius for better results
      }

      const response = await publicAPI.getNearbyShops(params);
      setShops(response.data.shops || []);
    } catch (err) {
      console.error('Failed to fetch shops:', err);
      setError('Failed to load nearby shops');
    } finally {
      setLoading(false);
    }
  };

  const handleGetDirections = (address: string) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  const filteredShops = shops.filter(shop => 
    shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shop.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 font-black uppercase tracking-widest text-[10px]">Locating Shops</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24">
      {/* Native Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 pt-safe-top">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-slate-600 active:scale-95 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-sm font-black uppercase tracking-widest text-slate-900">Nearby Shops</h1>
          <button className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-slate-600">
            <Filter className="w-5 h-5" />
          </button>
        </div>
        
        {/* Mobile Search Bar */}
        <div className="px-4 pb-4 max-w-lg mx-auto">
           <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input 
                type="text"
                placeholder="Search by name or area..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-slate-100/50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-sm font-medium"
              />
           </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-6">
        {error && (
          <div className="mb-6 bg-amber-50 border border-amber-100 text-amber-700 px-4 py-3 rounded-2xl text-xs font-bold flex items-center">
            <MapPin className="w-4 h-4 mr-2" />
            {error}
          </div>
        )}

        {filteredShops.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-[2.5rem] shadow-sm border border-slate-100">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
               <MapPin className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
              No shops found
            </h3>
            <p className="text-sm text-slate-500 mt-2 px-10 leading-relaxed font-medium">
              We couldn't find any Prindox shops in this area. Try searching for a different name.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredShops.map((shop, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                key={shop.upload_slug}
                className="bg-white rounded-[2rem] shadow-sm border border-slate-100 p-5 active:scale-[0.98] transition-all overflow-hidden relative"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-lg ${shop.is_open ? 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-blue-200' : 'bg-slate-300'}`}>
                      {shop.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-black text-slate-900 tracking-tight">
                        {shop.name}
                      </h3>
                      <div className="flex items-center mt-1">
                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${shop.is_open ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                        <span className={`text-[10px] font-black uppercase tracking-widest ${shop.is_open ? 'text-green-600' : 'text-red-500'}`}>
                          {shop.is_open ? 'Open Now' : 'Closed'}
                        </span>
                        {shop.distance_km && (
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-3">
                            • {shop.distance_km.toFixed(1)} km
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                    <Star className="w-3 h-3 text-amber-500 fill-current mr-1" />
                    <span className="text-[10px] font-black text-amber-700">4.8</span>
                  </div>
                </div>

                <p className="text-xs text-slate-500 font-medium line-clamp-2 mb-5 leading-relaxed">
                  {shop.address || 'Location information not available.'}
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleGetDirections(shop.address || shop.name)}
                    className="flex-1 bg-slate-100 text-slate-700 font-black py-3.5 rounded-xl text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all flex items-center justify-center"
                  >
                    <Navigation className="w-3.5 h-3.5 mr-2" />
                    Navigate
                  </button>
                  <Link
                    to={`/shop/${shop.upload_slug}`}
                    className={`flex-[1.5] text-center py-3.5 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all shadow-lg ${
                      shop.is_open
                        ? 'bg-blue-600 text-white shadow-blue-200 hover:bg-blue-700 active:translate-y-0.5'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                    }`}
                    onClick={(e) => !shop.is_open && e.preventDefault()}
                  >
                    {shop.is_open ? 'Send Files' : 'Closed'}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default FindShops;
